import Select from "react-select";

import { getOrganizations } from "@/api/endpoints/organization";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { cx } from "@/utils";
import { DropdownHeading, DropdownSeparator } from "@components/Dropdown";
import { useQuery } from "@tanstack/react-query";

import styles from "./Navigation.module.css";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";

const OrganizationSwitcher = ({ className, organizations }) => {
  const handleChange = (selected) => {
    window.location.pathname = `/a/${selected.value}/mentions/`;
  };

  const { data: orgData } = useOrganization();
  const { uuid: orgUuid } = orgData?.ok ? orgData.data : {};

  const orgOptions = organizations.reduce((acc, item) => {
    let group = acc.find((group) => group.label === item.status);
    if (!group) {
      group = { label: item.status, options: [] };
      acc.push(group);
    }

    group.options.push({
      label: item.name,
      value: item.uuid,
      selected: orgUuid === item.uuid,
    });

    return acc;
  }, []);

  return (
    <div
      id="organization-picker"
      data-testid="organization-picker"
      className={cx(styles.orgPicker, className)}
    >
      <Select
        classNamePrefix="react-select"
        id="organization-picker-select"
        menuPlacement="auto"
        value={orgUuid}
        options={orgOptions}
        placeholder="Gå till konto"
        onChange={handleChange}
      />
    </div>
  );
};

const StaffActions = () => {
  const { data: userData } = useUser();
  const { is_staff = false, default_organization = {} } = userData?.ok
    ? userData.data
    : {};
  const { data: orgData } = useOrganization();
  const { name, uuid, id: orgId } = orgData?.ok ? orgData.data : {};

  if (!is_staff) return null;

  const gotoDefault =
    default_organization?.uuid && uuid !== default_organization?.uuid;

  return (
    <>
      <DropdownSeparator />
      <a
        className={styles.staffAction}
        href="/internal/konton/new"
        target="_blank"
        rel="noreferrer"
      >
        Skapa nytt konto
      </a>
      <a
        className={styles.staffAction}
        href={`/internal/konton/${orgId}/`}
        target="_blank"
        rel="noreferrer"
      >{`Redigera ${name}`}</a>
      {gotoDefault && (
        <a
          className={styles.staffAction}
          href={`/a/${default_organization.uuid}/mentions/`}
        >{`Gå till ${default_organization.name}`}</a>
      )}
    </>
  );
};

export const UserSubMenu = ({ children, className }) => {
  const { data: userData } = useUser();
  const user = userData?.ok ? userData.data : {};
  const { data: organizations, isLoading } = useQuery({
    queryFn: async () => await getOrganizations(),
    queryKey: ["organizations"],
  });

  if (isLoading) return null;
  if (organizations.data?.length <= 1) return null;

  return (
    <div>
      <Popover>
        <PopoverTrigger asChild data-testid="toggle-organization-menu">
          <button className={cx(styles.userSubMenu, className)}>
            {children}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="popover-menu bg-white pt-3 rounded-lg shadow border border-stone-200"
        >
          <DropdownHeading>{user?.email}</DropdownHeading>
          <StaffActions organizations={organizations.data} />
          <DropdownSeparator />
          <OrganizationSwitcher organizations={organizations.data} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
